import {
  type DatePickerProps,
  DatePicker as PantryDatePicker,
} from '@side/pantry';
import { addDays, addMonths } from 'date-fns';
import type { FocusEvent } from 'react';
import {
  TrackInteractionsEvent,
  useTrackInteractionsAnalytics,
} from '$analytics/useTrackAnalytics/domains/interactions';

export function DatePickerWithEventTracking({
  eventContainer,
  name,
  value,
  onAccept,
  onOpen,
  onMonthChange,
  slotProps,
  FirstChipProps,
  SecondChipProps,
  ThirdChipProps,
  ...props
}: DatePickerProps & { eventContainer: string }) {
  const trackEvent = useTrackInteractionsAnalytics();

  const eventData = {
    eventContainer,
    value,
    fieldName: name,
  };

  const textFieldSlotProps =
    typeof slotProps?.textField !== 'function'
      ? slotProps?.textField
      : undefined;

  return (
    <PantryDatePicker
      FirstChipProps={{
        label: 'Today',
        onClick: () => {
          const nextDate = new Date();
          props.onChange?.(nextDate, {
            validationError: null,
            source: 'unknown',
          });
          trackEvent(TrackInteractionsEvent.DatePickerAction, {
            ...eventData,
            eventTarget: 'today_chip',
            eventType: 'click',
            task: 'update',
            chip: 'today',
          });
        },
        ...FirstChipProps,
      }}
      SecondChipProps={{
        label: '+30 days',
        onClick: () => {
          const currentDate = value ?? new Date();
          const nextDate = addDays(currentDate, 30);
          props.onChange?.(nextDate, {
            validationError: null,
            source: 'unknown',
          });
          trackEvent(TrackInteractionsEvent.DatePickerAction, {
            ...eventData,
            eventTarget: 'thirty_days_ago_chip',
            eventType: 'click',
            task: 'update',
            chip: '+30 days',
          });
        },
        ...SecondChipProps,
      }}
      ThirdChipProps={{
        label: '+6 months',
        onClick: () => {
          const currentDate = value ?? new Date();
          const nextDate = addMonths(currentDate, 6);
          props.onChange?.(nextDate, {
            validationError: null,
            source: 'unknown',
          });
          trackEvent(TrackInteractionsEvent.DatePickerAction, {
            ...eventData,
            eventTarget: 'six_months_ago_chip',
            eventType: 'click',
            task: 'update',
            chip: '+6 months',
          });
        },
        ...ThirdChipProps,
      }}
      onMonthChange={(month) => {
        onMonthChange?.(month);
        trackEvent(TrackInteractionsEvent.DatePickerAction, {
          ...eventData,
          eventTarget: 'month_arrow',
          eventType: 'click',
          task: 'navigate',
          chip: 'next_month',
        });
      }}
      onAccept={(e, context) => {
        onAccept?.(e, context);
        trackEvent(TrackInteractionsEvent.DatePickerAction, {
          ...eventData,
          eventTarget: 'calendar_date',
          eventType: 'click',
          task: 'update',
        });
      }}
      onOpen={() => {
        trackEvent(TrackInteractionsEvent.DatePickerAction, {
          ...eventData,
          eventTarget: 'calendar_button',
          eventType: 'click',
          task: 'mount',
        });
        onOpen?.();
      }}
      slotProps={{
        ...slotProps,
        textField: {
          ...textFieldSlotProps,
          onBlur: (e: FocusEvent<HTMLDivElement>) => {
            trackEvent(TrackInteractionsEvent.DatePickerAction, {
              ...eventData,
              eventTarget: 'date_field',
              eventType: 'blur',
              task: 'modify_input',
            });
            textFieldSlotProps?.onBlur?.(e);
          },
        },
      }}
      {...props}
      value={value}
    />
  );
}
